import React, { useState, useEffect, useRef } from 'react';
import { Modal, Typography, Button, Space, App, Spin, Input } from 'antd';
import { ShoppingCartOutlined, SearchOutlined, WarningOutlined, DownOutlined, UpOutlined, ArrowLeftOutlined, TeamOutlined } from '@ant-design/icons';
import SeatPill from '../../common/SeatPill';
import { seatLabel, maxSeatIn, MAX_SEATS } from '../../../utils/seats';
import { Table } from '../../../types/tables';
import { CreateOrderItemRequest } from '../../../types/orders';
import { Product, ProductOrderItem } from '../../../types/products';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { setSelectedCategory } from '../../../store/slices/productsSlice';
import {
  useGetCategoriesQuery,
  useGetProductsQuery,
  useGetProductsByCategoryQuery,
} from '../../../services/productsApi';
import {
  useGetActiveOrderByTableQuery,
  useCreateOrderMutation,
  useAddOrderItemMutation,
  useAddMultipleOrderItemsMutation,
} from '../../../services/ordersApi';
import { useGetKitchenNotesQuery } from '../../../services/kitchenApi';
import {
  useGetActiveProductionCentersQuery,
} from '../../../services/productionCenterApi';
import { useGetBoolSettingQuery } from '../../../services/settingsApi';
import { useCloudPrint } from '../../../hooks/useCloudPrint';
import { useGetActiveDailyMenusQuery } from '../../../services/dailyMenuApi';
import { useUnsavedChangesWarning } from '../../../hooks/useUnsavedChangesWarning';
import { useRestaurantTimeZone } from '../../../hooks/useTimeZone';
import { printAgentService } from '../../../services/printAgentService';
import { shouldUseCloudPrint } from '../../../utils/cloudPrintRoute';
import { bluetoothPrintService } from '../../../services/bluetoothPrintService';
import { newClientId } from '../../../utils/clientId';
import { getRetryableWriteErrorMessage as describeSendError } from '../../../utils/errorHelpers';
import CategorySelector from '../../orders/CategorySelector';
import ProductGrid from '../../orders/ProductGrid';
import OrderSummary from '../../orders/OrderSummary';
import ProductVariantsModal from '../../orders/modals/ProductVariantsModal';
import DailyMenuSelectionModal from '../../orders/modals/DailyMenuSelectionModal';
import '../../orders/orders.css';

const { Title, Text } = Typography;

export interface ComandearContentProps {
  table?: Table;
  visible: boolean;
  onClose: () => void;
}

export const ComandearContent: React.FC<ComandearContentProps> = ({
  table,
  visible,
  onClose,
}) => {
  const { message } = App.useApp();
  const dispatch = useAppDispatch();
  
  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [variantsModalVisible, setVariantsModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [localOrderItems, setLocalOrderItems] = useState<ProductOrderItem[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [, setActiveTab] = useState('products');
  const [printAgentAvailable, setPrintAgentAvailable] = useState(false);
  const [bluetoothAvailable, setBluetoothAvailable] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [dailyMenuModalVisible, setDailyMenuModalVisible] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [editingDailyMenu, setEditingDailyMenu] = useState(false);
  const [kitchenNotesExpanded, setKitchenNotesExpanded] = useState(false);
  const [existingItemsExpanded, setExistingItemsExpanded] = useState(false);
  // Ids del envío que aún no se confirma. Se conservan entre intentos para que el
  // servidor reconozca el reintento; se limpian al confirmarse o al abrir de nuevo.
  const pendingOrderRef = useRef<{ tableId: string; orderId: string } | null>(null);
  const pendingItemsRef = useRef<{ keys: string[]; ids: string[] } | null>(null);
  // Cuentas por comensal: comensal activo al tocar productos (null = "Todos",
  // compartido por la mesa), comensales agregados con "+" más allá de los
  // capturados al abrir la mesa, y si el mesero encendió la barra a mano.
  const [activeSeat, setActiveSeat] = useState<number | null>(null);
  const [extraSeats, setExtraSeats] = useState(0);
  const [seatsEnabled, setSeatsEnabled] = useState(false);

  // Redux state
  const { selectedCategoryId } = useAppSelector(state => state.products);
  const { formatInTimeZone } = useRestaurantTimeZone();

  // API queries
  const { data: categories = [], isLoading: categoriesLoading } = useGetCategoriesQuery({ activeOnly: true });
  
  // Get all products when no category selected
  const { 
    data: allProducts = [], 
    isLoading: allProductsLoading 
  } = useGetProductsQuery({ availableOnly: false });
  
  // Get products by category when category is selected
  const { 
    data: categoryProducts = [], 
    isLoading: categoryProductsLoading 
  } = useGetProductsByCategoryQuery(selectedCategoryId || '', {
    skip: !selectedCategoryId
  });
  
  // Sort products: available first, then unavailable
  const sortByAvailability = (products: Product[]) =>
    [...products].sort((a, b) => {
      if (a.isAvailable === b.isAvailable) return 0;
      return a.isAvailable ? -1 : 1;
    });

  // Use the appropriate products array and loading state
  // Si hay búsqueda, buscar en todos los productos; si no, aplicar filtro de categoría
  const products = sortByAvailability(
    searchQuery
      ? allProducts
      : (selectedCategoryId ? categoryProducts : allProducts)
  );
  const productsLoading = selectedCategoryId ? categoryProductsLoading : allProductsLoading;
  
  const {
    data: activeOrder,
    isLoading: orderLoading,
    refetch: refetchOrder
  } = useGetActiveOrderByTableQuery(table?.id || '', {
    skip: !table?.id || !visible
  });

  // Production centers: solo para decidir qué se imprime. El gate del KDS
  // (centros O KitchenDisplayEnabled) ahora vive en el backend, junto a la creación
  // de la comanda — aquí no servía de nada si la petición nunca salía.
  useGetActiveProductionCentersQuery();
  const { data: printComandasSetting } = useGetBoolSettingQuery('ImprimirComandas');
  const { data: menuDelDiaSetting } = useGetBoolSettingQuery('MenuDelDiaActivo');
  const { data: activeDailyMenus = [] } = useGetActiveDailyMenusQuery(undefined, {
    skip: !menuDelDiaSetting?.value || !visible,
  });
  const hasActiveDailyMenu = (menuDelDiaSetting?.value ?? false) && activeDailyMenus.length > 0;

  // Cloud print
  const { data: cloudPrintSetting } = useGetBoolSettingQuery('CloudPrintEnabled');
  // Nube solo con servidor y con la orden ya en él: ver shouldUseCloudPrint.
  const cloudPrintSettingOn = cloudPrintSetting?.value ?? false;
  const { printKitchenOrder: cloudPrintKitchenOrder } = useCloudPrint();

  // Kitchen notes query - alerts from kitchen staff
  const { data: kitchenNotes = [] } = useGetKitchenNotesQuery(undefined, {
    skip: !visible, // Only fetch when modal is visible
  });

  // Mutations
  const [createOrder, { isLoading: createOrderLoading }] = useCreateOrderMutation();
  const [, { isLoading: addOrderItemLoading }] = useAddOrderItemMutation();
  const [addMultipleOrderItems, { isLoading: addMultipleOrderItemsLoading }] = useAddMultipleOrderItemsMutation();

  // Initialize category selection to "all" by default
  useEffect(() => {
    if (categories.length > 0 && !selectedCategoryId) {
      dispatch(setSelectedCategory(null)); // null = "Todos los productos"
    }
  }, [categories, selectedCategoryId, dispatch]);

  // Check for mobile screen size
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Reset search when modal opens/closes
  useEffect(() => {
    if (visible) {
      setSearchQuery('');
      setLocalOrderItems([]);
      // Un carrito nuevo es un envío nuevo aunque sea idéntico al que falló antes: la
      // orden ya se recargó, y reusar los Ids haría pasar una ronda nueva por reintento.
      pendingOrderRef.current = null;
      pendingItemsRef.current = null;
      setActiveTab('products');
      // Ensure we start with "Todos los productos" when opening
      dispatch(setSelectedCategory(null));
    }
  }, [visible, dispatch]);

  // Check for available print services when modal opens
  useEffect(() => {
    const checkPrintServices = async () => {
      // Check Web Bluetooth API (Android/Chrome)
      const btAvailable = bluetoothPrintService.isAvailable();
      setBluetoothAvailable(btAvailable);

      // Check Print Agent (Windows)
      const agentAvailable = await printAgentService.isAvailable();
      setPrintAgentAvailable(agentAvailable);
    };
    if (visible) {
      checkPrintServices();
    }
  }, [visible]);

  const handleCategoryChange = (categoryId: string | null) => {
    dispatch(setSelectedCategory(categoryId));
    setSearchQuery('');
  };

  // Search runs over ALL products, so the category pill resets to "Todos"
  // to keep the visual state consistent with the results.
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (value && selectedCategoryId) {
      dispatch(setSelectedCategory(null));
    }
  };

  // Cada renglón nuevo se etiqueta con el comensal activo (null = compartido).
  const stampSeat = (orderItem: ProductOrderItem): ProductOrderItem =>
    orderItem.seatNumber === undefined ? { ...orderItem, seatNumber: activeSeat } : orderItem;

  const handleProductAdd = (rawItem: ProductOrderItem) => {
    const orderItem = stampSeat(rawItem);
    setLocalOrderItems(prev => {
      // Group duplicate items: same product, same seat, no notes, no variants, no daily menu
      const canGroup = !orderItem.notes &&
        orderItem.selectedVariants.length === 0 &&
        !orderItem.variantSelections?.length &&
        !orderItem.dailyMenuId;

      if (canGroup) {
        // Dos comensales con el mismo refresco NO se juntan: perderían quién pidió qué
        const existingIndex = prev.findIndex(item =>
          item.productId === orderItem.productId &&
          (item.seatNumber ?? null) === (orderItem.seatNumber ?? null) &&
          !item.notes &&
          item.selectedVariants.length === 0 &&
          !item.variantSelections?.length &&
          !item.dailyMenuId
        );

        if (existingIndex >= 0) {
          return prev.map((item, i) =>
            i === existingIndex
              ? { ...item, quantity: item.quantity + orderItem.quantity }
              : item
          );
        }
      }

      return [...prev, orderItem];
    });
    // No toast per add: the card badge and the summary panel are the feedback.
  };


  const clearEditState = () => {
    setEditingItemIndex(null);
    setEditingDailyMenu(false);
  };

  const handleVariantsSubmit = (orderItem: ProductOrderItem) => {
    if (editingItemIndex !== null) {
      // Al editar se conserva el comensal del renglón original
      setLocalOrderItems(prev =>
        prev.map((item, i) => i === editingItemIndex ? { ...orderItem, seatNumber: item.seatNumber } : item)
      );
      clearEditState();
      message.success(`${orderItem.productName} actualizado`);
    } else {
      setLocalOrderItems(prev => [...prev, stampSeat(orderItem)]);
    }
    setVariantsModalVisible(false);
  };

  const handleDailyMenuAdd = (orderItem: ProductOrderItem) => {
    if (editingItemIndex !== null) {
      setLocalOrderItems(prev =>
        prev.map((item, i) => i === editingItemIndex ? { ...orderItem, seatNumber: item.seatNumber } : item)
      );
      clearEditState();
      message.success(`${orderItem.productName} actualizado`);
    } else {
      setLocalOrderItems(prev => [...prev, stampSeat(orderItem)]);
    }
  };

  // Tocar la etiqueta del renglón lo pasa al siguiente comensal
  // (Todos → C1 → C2 … → Todos) sin abrir nada.
  const handleCycleSeat = (index: number) => {
    setLocalOrderItems(prev =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const current = item.seatNumber ?? 0;
        const next = current >= seatCount ? null : current + 1;
        return { ...item, seatNumber: next };
      })
    );
  };

  const handleEditItem = (index: number) => {
    const item = localOrderItems[index];
    if (item.dailyMenuId) {
      setEditingItemIndex(index);
      setEditingDailyMenu(true);
    } else {
      const product = allProducts?.find(p => p.id === item.productId);
      if (product) {
        setEditingItemIndex(index);
        setSelectedProduct(product);
        setVariantsModalVisible(true);
      } else {
        message.error('No se encontró el producto para editar');
      }
    }
  };

  const handleRemoveItem = (index: number) => {
    setLocalOrderItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateQuantity = (index: number, quantity: number) => {
    if (quantity <= 0) {
      handleRemoveItem(index);
      return;
    }
    setLocalOrderItems(prev =>
      prev.map((item, i) => i === index ? { ...item, quantity } : item)
    );
  };

  const handleSendToKitchen = async () => {
    if (isSending) return;

    if (!table || localOrderItems.length === 0) {
      message.warning('No hay productos para enviar');
      return;
    }

    setIsSending(true);
    try {
      let orderId = activeOrder?.id;
      let currentOrder = activeOrder;

      // Create order if doesn't exist
      if (!activeOrder) {
        try {
          // Mismo Id mientras no se confirme el alta en esta mesa: si el primer intento
          // sí creó la orden pero la respuesta se perdió, el reintento la recupera en
          // vez de chocar con "la mesa ya tiene una orden abierta".
          if (pendingOrderRef.current?.tableId !== table.id) {
            pendingOrderRef.current = { tableId: table.id, orderId: newClientId() };
          }
          const newOrderData = {
            id: pendingOrderRef.current.orderId,
            tableId: table.id,
            orderItems: []
          };
          const newOrder = await createOrder(newOrderData).unwrap();
          orderId = newOrder.id;
          currentOrder = newOrder;
        } catch (orderError: any) {
          console.error('Error creating order:', orderError);
          throw new Error(describeSendError(orderError, 'No se pudo abrir la orden'));
        }
      }

      // Prepare all items for bulk insert
      const orderItemsData: CreateOrderItemRequest[] = localOrderItems.map((item, index) => {
        const orderItemData: CreateOrderItemRequest = {
          productId: item.productId,
          quantity: item.quantity,
          notes: item.notes || undefined,
          seatNumber: item.seatNumber ?? undefined,
          variantIds: item.selectedVariants.map(v => v.id),
          variantSelections: item.variantSelections?.map(v => ({ variantId: v.variantId, quantity: v.quantity })),
          isCancelled: false,
          dailyMenuId: item.dailyMenuId || undefined,
          dailyMenuSelections: item.dailyMenuSelections?.map(s => ({ courseId: s.courseId, optionId: s.optionId })),
        };

        return orderItemData;
      });

      // Ids de renglón: cada renglón que ya iba en el intento anterior conserva su Id.
      // Así un reintento tras un timeout (renglones guardados, respuesta perdida) no
      // duplica productos ni comanda. Se conserva POR RENGLÓN y no por carrito entero:
      // si la cajera agrega o edita algo después de un envío que sí se guardó, los
      // renglones ya guardados llevan su Id de antes y el servidor rechaza la mezcla
      // ("recarga la mesa") en vez de volver a insertarlos todos con Ids nuevos.
      const lineKeys = orderItemsData.map(item => JSON.stringify([orderId, item]));
      const reusable = new Map<string, string[]>();
      const previous = pendingItemsRef.current;
      previous?.keys.forEach((key, i) => {
        reusable.set(key, [...(reusable.get(key) ?? []), previous.ids[i]]);
      });
      const itemIds = lineKeys.map(key => reusable.get(key)?.shift() ?? newClientId());
      pendingItemsRef.current = { keys: lineKeys, ids: itemIds };
      orderItemsData.forEach((item, i) => { item.id = itemIds[i]; });

      // Send all items in a single request.
      //
      // La comanda la crea el backend DENTRO de esta misma transacción; ya no se manda desde
      // aquí. Antes eran dos peticiones: esta guardaba los renglones y una segunda mandaba la
      // comanda, con su fallo tragado por un console.warn. Cuando la segunda no salía —red,
      // 500, la tablet dormida— el producto quedaba cobrable y sin comanda para siempre, y ni
      // "Reenviar" podía rescatarlo porque ese modal lee de KitchenOrders, no de la orden.
      // El Mirador, orden 3275, 16-ago-2026. Ver IKitchenOrderFactory en el backend.
      try {
        await addMultipleOrderItems({
          orderId: orderId!,
          items: orderItemsData
        }).unwrap();
      } catch (bulkError: any) {
        console.error('❌ Bulk insert failed:', bulkError);
        console.error('Full error response:', {
          status: bulkError?.status,
          data: bulkError?.data,
          message: bulkError?.message,
          originalData: bulkError?.originalData
        });

        // Un solo aviso: lo muestra el catch de abajo.
        throw new Error(describeSendError(bulkError, 'No se pudieron agregar los productos'));
      }

      // Guardado: el siguiente envío (otro carrito u otra mesa) lleva Ids nuevos.
      pendingOrderRef.current = null;
      pendingItemsRef.current = null;

      // Check if printing comandas is enabled (global setting)
      const shouldPrintComandas = printComandasSetting?.value ?? true;

      // Then try to print (don't block on failure) - only if setting is enabled
      if (currentOrder && shouldPrintComandas) {
        // Nube o equipo, decidido ahora con la orden en la mano (ver shouldUseCloudPrint).
        const printInCloud = shouldUseCloudPrint(cloudPrintSettingOn, currentOrder);
        try {
          // Filter items that should be printed based on production center's PrintTicket setting
          // If product has no production centers, print by default
          // If product has production centers, only print if at least one has PrintTicket=true
          const itemsToPrint = localOrderItems.filter(item => {
            if (!item.productionCenters || item.productionCenters.length === 0) {
              return true; // No production centers = print by default
            }
            return item.productionCenters.some(pc => pc.printTicket);
          });

          // Only proceed with printing if there are items to print
          if (itemsToPrint.length > 0) {
            // Separate regular items from daily menu items
            const regularItems = itemsToPrint.filter(item => !item.dailyMenuSelections?.length);
            const dailyMenuItems = itemsToPrint.filter(item => item.dailyMenuSelections?.length);

            // Convert regular items to OrderItem format, grouping duplicates
            const regularGroupMap = new Map<string, { item: typeof regularItems[0]; qty: number }>();
            for (const item of regularItems) {
              const variantsKey = item.selectedVariants.map(v => v.name).sort().join(',');
              const selectionsKey = (item.variantSelections || []).map(v => `${v.variantId}:${v.quantity}`).sort().join(',');
              const key = `${item.productName}|${variantsKey}|${selectionsKey}|${item.notes || ''}|${item.seatNumber ?? ''}`;
              const existing = regularGroupMap.get(key);
              if (existing) {
                existing.qty += item.quantity;
              } else {
                regularGroupMap.set(key, { item, qty: item.quantity });
              }
            }
            const regularOrderItems = Array.from(regularGroupMap.values()).map(({ item, qty }) => ({
              id: '',
              orderId: currentOrder!.id,
              productId: item.productId,
              productName: item.productName,
              quantity: qty,
              unitPrice: item.unitPrice,
              total: qty * item.unitPrice,
              variants: item.selectedVariants.map(v => ({
                variantId: v.id,
                variantName: v.name,
                priceModifier: v.priceModifier
              })),
              notes: item.notes,
              seatNumber: item.seatNumber ?? undefined,
              isCancelled: false,
              createdAt: new Date().toISOString()
            }));

            // Print Agent y Bluetooth arman el ticket en el cliente y su formato
            // no conoce comensales: el marcador va en el nombre ("C2: Tacos").
            const withSeatInName = (batch: typeof regularOrderItems) =>
              batch.map(i => (i.seatNumber ? { ...i, productName: `${seatLabel(i.seatNumber)}: ${i.productName}` } : i));

            // Aggregate daily menu items by COURSE for print:
            // ENTRADA (×5): 3× Sopa, 2× Consomé
            // PLATO FUERTE (×5): 5× Pollo
            //   Nota: sin cebolla | extra picante
            const courseAgg = new Map<string, { courseName: string; sortOrder: number; options: Map<string, number> }>();
            for (const item of dailyMenuItems) {
              for (const sel of item.dailyMenuSelections || []) {
                if (!sel.optionName) continue;
                let courseData = courseAgg.get(sel.courseName);
                if (!courseData) {
                  courseData = { courseName: sel.courseName, sortOrder: sel.sortOrder ?? 0, options: new Map() };
                  courseAgg.set(sel.courseName, courseData);
                }
                const currentQty = courseData.options.get(sel.optionName) || 0;
                courseData.options.set(sel.optionName, currentQty + item.quantity);
              }
            }

            // Collect unique notes from daily menu items
            const dailyMenuNotes = dailyMenuItems
              .filter(item => item.notes)
              .map(item => item.notes!);
            const uniqueDailyMenuNotes = Array.from(new Set(dailyMenuNotes)).join(' | ');

            // Convert aggregated courses to OrderItem format for printing
            const sortedCourses = Array.from(courseAgg.values())
              .sort((a, b) => a.sortOrder - b.sortOrder);
            const dailyMenuOrderItems: typeof regularOrderItems = sortedCourses
              .map((course, idx) => {
                const totalQty = Array.from(course.options.values()).reduce((sum, q) => sum + q, 0);
                const optionsList = Array.from(course.options.entries())
                  .map(([name, qty]) => qty > 1 ? `${qty}× ${name}` : name);
                const isLast = idx === sortedCourses.length - 1;
                return {
                  id: '',
                  orderId: currentOrder!.id,
                  productId: '',
                  productName: course.courseName.toUpperCase(),
                  quantity: totalQty,
                  unitPrice: 0,
                  total: 0,
                  variants: optionsList.map(opt => ({ variantId: '', variantName: opt, priceModifier: 0 })),
                  notes: (isLast && uniqueDailyMenuNotes) ? uniqueDailyMenuNotes : undefined as string | undefined,
                  seatNumber: undefined as number | undefined,
                  isCancelled: false,
                  createdAt: new Date().toISOString()
                };
              });

            // Mirror the backend split: when a comanda mixes regulars and daily-menu, the
            // backend now creates two KitchenOrders so the kitchen sees two cards. We also
            // print two separate tickets so each card matches a physical ticket.
            const ticketBatches: typeof regularOrderItems[] = [];
            if (regularOrderItems.length > 0) ticketBatches.push(regularOrderItems);
            if (dailyMenuOrderItems.length > 0) ticketBatches.push(dailyMenuOrderItems);

            const tableLabel = table ? `${table.areaName} - ${table.number}` : '';
            let printedCount = 0;
            let printError: string | null = null;

            // Try Cloud Print first (if enabled). Mirror the batch split: one
            // cloud job per ticketBatch so each kitchen card prints separately.
            if (printInCloud) {
              for (const batch of ticketBatches) {
                await cloudPrintKitchenOrder({
                  orderId: currentOrder!.id,
                  tableLabel: tableLabel,
                  waiterName: currentOrder!.assignedWaiterName || '',
                  items: batch.map(item => ({
                    productId: item.productId,
                    productName: item.productName,
                    quantity: item.quantity,
                    variants: (item.variants || []).map(v => v.variantName),
                    notes: item.notes,
                    seatNumber: item.seatNumber ?? undefined,
                    productionCenterId: undefined,
                  })),
                });
              }
              message.success(`${localOrderItems.length} producto(s) enviado(s) a cocina`);
            }
            // Try Print Agent (Windows)
            else if (printAgentAvailable) {
              for (const batch of ticketBatches) {
                const result = await printAgentService.printKitchenOrder(
                  currentOrder,
                  tableLabel,
                  currentOrder.assignedWaiterName || '',
                  withSeatInName(batch) as any
                );
                if (result.success) {
                  printedCount++;
                } else {
                  printError = result.message;
                  console.error('Print Agent error:', result.error);
                }
              }
            }
            // Try Web Bluetooth if Print Agent not available (Android/Chrome)
            else if (bluetoothAvailable) {
              for (const batch of ticketBatches) {
                const result = await bluetoothPrintService.printKitchenOrder(
                  currentOrder,
                  tableLabel,
                  currentOrder.assignedWaiterName || '',
                  withSeatInName(batch) as any
                );
                if (result.success) {
                  printedCount++;
                } else {
                  printError = result.message;
                  console.error('Bluetooth error:', result.error);
                }
              }
            }

            if (printInCloud) {
              // Cloud Print already reported success above; nothing to do here.
            } else if (printAgentAvailable || bluetoothAvailable) {
              if (printError === null && printedCount > 0) {
                message.success(`${localOrderItems.length} producto(s) enviado(s) a cocina, ${itemsToPrint.length} impreso(s)`);
              } else {
                message.warning(`Productos enviados a cocina pero falló la impresión: ${printError}`);
              }
            } else {
              // No print service available
              console.warn('No print service available');
              message.success(`${localOrderItems.length} producto(s) enviado(s) a cocina`);
            }
          } else {
            // No items need printing (all have PrintTicket=false)
            message.success(`${localOrderItems.length} producto(s) enviado(s) a cocina`);
          }
        } catch (printError: any) {
          // Log print error but continue anyway
          console.warn('Print failed (continuing anyway):', printError);
          message.success(`${localOrderItems.length} producto(s) enviado(s) a cocina (impresión no disponible)`);
        }
      } else if (currentOrder && !shouldPrintComandas) {
        // Printing disabled by global setting
        message.success(`${localOrderItems.length} producto(s) enviado(s) a cocina`);
      } else {
        message.success(`${localOrderItems.length} producto(s) enviado(s) a cocina`);
      }

      // Clear local items and refresh
      setLocalOrderItems([]);
      refetchOrder();

      // Close modal after successful submission
      onClose();
    } catch (error: any) {
      console.error('Error al enviar orden:', error);
      console.error('Error details:', {
        message: error?.message,
        data: error?.data,
        status: error?.status,
        originalStatus: error?.originalStatus,
        error: error?.error,
        stack: error?.stack
      });

      message.error(error?.message || 'No se pudieron enviar los productos a cocina', 10);
    } finally {
      setIsSending(false);
    }
  };


  const { confirmDiscard } = useUnsavedChangesWarning({
    hasUnsavedChanges: localOrderItems.length > 0,
  });

  const handleCancel = () => {
    confirmDiscard(() => {
      setLocalOrderItems([]);
      setSearchQuery('');
      // Reset to "Todos los productos" when closing
      dispatch(setSelectedCategory(null));
      onClose();
    });
  };

  // Products of inactive categories must not be orderable, even from "Todos"/search.
  // Categories arrive already filtered by activeOnly; while they load (empty set)
  // we fail open so the product grid doesn't flash empty.
  const activeCategoryIds = new Set(categories.map(c => c.id));

  const filteredProducts = products.filter(product =>
    product.isActive &&
    (activeCategoryIds.size === 0 || activeCategoryIds.has(product.categoryId)) && (
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const totalNewItems = localOrderItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalNewAmount = localOrderItems.reduce((sum, item) =>
    sum + (item.quantity * item.unitPrice), 0
  );

  // productId -> pending units, for the badge on each product card
  const cartQuantities = localOrderItems.reduce<Record<string, number>>((acc, item) => {
    acc[item.productId] = (acc[item.productId] || 0) + item.quantity;
    return acc;
  }, {});

  // Items already sent to kitchen (shown collapsed for context)
  const existingItems = (activeOrder?.orderItems || []).filter(item => !item.isCancelled);
  const existingTotal = existingItems.reduce((sum, item) => sum + item.total, 0);

  const existingItemsSection = existingItems.length > 0 && (
    <div style={{ borderTop: '1px solid var(--border-color)', flexShrink: 0, backgroundColor: 'var(--bg-primary)' }}>
      <div
        onClick={() => setExistingItemsExpanded(prev => !prev)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '10px 16px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Text strong style={{ fontSize: 13 }}>
          Ya en la orden ({existingItems.length}) · ${existingTotal.toFixed(2)}
        </Text>
        {existingItemsExpanded
          ? <UpOutlined style={{ fontSize: 10, color: 'var(--color-neutral-text)' }} />
          : <DownOutlined style={{ fontSize: 10, color: 'var(--color-neutral-text)' }} />
        }
      </div>
      {existingItemsExpanded && (
        <div style={{ maxHeight: 180, overflowY: 'auto', padding: '0 16px 10px' }}>
          {existingItems.map(item => (
            <div
              key={item.id}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '3px 0' }}
            >
              <Text style={{ fontSize: 13 }}>
                {item.seatNumber && <SeatPill seatNumber={item.seatNumber} size="small" style={{ marginRight: 6 }} />}
                {item.quantity}x {item.productName}
                {item.dispatchedAt && (
                  <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                    despachado {formatInTimeZone(item.dispatchedAt, 'HH:mm')}
                  </Text>
                )}
              </Text>
              <Text style={{ fontSize: 13, whiteSpace: 'nowrap' }}>${item.total.toFixed(2)}</Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // --- Comensales ---
  // Cuántas pills mostrar: los comensales capturados al abrir la mesa, más los
  // que ya aparezcan en renglones (por si la mesa se abrió con menos), más los
  // agregados con "+". Con un solo comensal la barra no aporta y se esconde
  // detrás de un botón, para no estorbar a quien no divide por persona.
  const guestCount = (activeOrder?.adults ?? 0) + (activeOrder?.children ?? 0);
  const seatCount = Math.min(
    MAX_SEATS,
    Math.max(guestCount, maxSeatIn(existingItems), maxSeatIn(localOrderItems), seatsEnabled ? 2 : 0) + extraSeats
  );
  const seatsInUse = seatCount >= 2 || maxSeatIn(existingItems) > 0 || maxSeatIn(localOrderItems) > 0;
  const showSeatBar = seatsEnabled || seatsInUse;
  const effectiveActiveSeat = activeSeat !== null && activeSeat > seatCount ? null : activeSeat;

  const seatBar = showSeatBar ? (
    <div className="seat-bar" role="tablist" aria-label="Pidiendo para">
      <span className="seat-bar__label">Pidiendo para</span>
      <button
        type="button"
        className={`seat-bar__pill${effectiveActiveSeat === null ? ' seat-bar__pill--active' : ''}`}
        onClick={() => setActiveSeat(null)}
        aria-pressed={effectiveActiveSeat === null}
      >
        Todos
      </button>
      {Array.from({ length: seatCount }, (_, i) => i + 1).map(seat => (
        <button
          key={seat}
          type="button"
          className={`seat-bar__pill${effectiveActiveSeat === seat ? ' seat-bar__pill--active' : ''}`}
          onClick={() => setActiveSeat(seat)}
          aria-pressed={effectiveActiveSeat === seat}
        >
          {seatLabel(seat)}
        </button>
      ))}
      {seatCount < MAX_SEATS && (
        <button
          type="button"
          className="seat-bar__pill seat-bar__pill--add"
          onClick={() => {
            setExtraSeats(n => n + 1);
            setActiveSeat(seatCount + 1);
          }}
          aria-label="Agregar comensal"
        >
          +
        </button>
      )}
    </div>
  ) : (
    <div className="seat-bar" style={{ justifyContent: 'flex-end', padding: '4px 12px' }}>
      <Button
        type="text"
        size="small"
        icon={<TeamOutlined />}
        onClick={() => {
          setSeatsEnabled(true);
          setActiveSeat(1);
        }}
        style={{ color: 'var(--text-secondary)' }}
      >
        Pedir por comensal
      </Button>
    </div>
  );

  return (
    <>
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {isMobile ? (
            // Mobile Layout
            <>
            {/* Mobile Header — un solo renglón: en el teléfono cada línea que se
                gana aquí es una línea más de productos a la vista. */}
            <div style={{
              padding: '6px 10px',
              borderBottom: '1px solid var(--border-color)',
              backgroundColor: 'var(--bg-secondary)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleCancel}
                size="small"
                type="text"
                aria-label="Volver"
              />
              <Text
                strong
                style={{
                  fontSize: 15,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                }}
              >
                Mesa {table?.number}
              </Text>
              {table?.areaName && (
                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                  }}
                >
                  {table.areaName}
                </Text>
              )}
              {totalNewItems > 0 && (
                <Text
                  style={{
                    color: '#52c41a',
                    fontWeight: 'bold',
                    fontSize: 12,
                    marginLeft: 'auto',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  +{totalNewItems} agregado{totalNewItems === 1 ? '' : 's'}
                </Text>
              )}
            </div>

            {/* Kitchen Notes - Mobile */}
            {kitchenNotes.length > 0 && (
              <div
                style={{
                  margin: '4px 8px 0 8px',
                  borderRadius: 6,
                  border: '1px solid #faad14',
                  backgroundColor: 'var(--bg-warning-light)',
                  overflow: 'hidden',
                }}
              >
                <div
                  onClick={() => setKitchenNotesExpanded(!kitchenNotesExpanded)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 10px',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <WarningOutlined style={{ fontSize: 14, color: '#faad14' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>
                    Avisos de Cocina ({kitchenNotes.length})
                  </span>
                  {kitchenNotesExpanded
                    ? <UpOutlined style={{ fontSize: 10, color: 'var(--color-neutral-text)' }} />
                    : <DownOutlined style={{ fontSize: 10, color: 'var(--color-neutral-text)' }} />
                  }
                </div>
                {kitchenNotesExpanded && (
                  <ul style={{
                    margin: 0,
                    paddingLeft: 36,
                    paddingBottom: 6,
                    paddingRight: 10,
                    fontSize: 11,
                    maxHeight: 120,
                    overflowY: 'auto',
                  }}>
                    {kitchenNotes.map(note => (
                      <li key={note.id} style={{ marginBottom: 1, lineHeight: 1.3 }}>{note.message}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Mobile Content */}
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden'
            }}>
              {/* Products Section */}
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0
              }}>
                {/* Category Selector - Fixed */}
                <div style={{
                  padding: '8px 12px',
                  borderBottom: '1px solid var(--border-color)',
                  flexShrink: 0
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: hasActiveDailyMenu ? 8 : 0 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <CategorySelector
                        categories={categories}
                        selectedCategoryId={selectedCategoryId}
                        onCategoryChange={handleCategoryChange}
                        loading={categoriesLoading}
                        compact={true}
                        horizontalScroll={true}
                      />
                    </div>
                    {hasActiveDailyMenu && (
                      <Button
                        type="primary"
                        onClick={() => setDailyMenuModalVisible(true)}
                        style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16', fontWeight: 'bold', flexShrink: 0 }}
                        size="middle"
                      >
                        Menu del Dia
                      </Button>
                    )}
                  </div>

                  {/* Search Box */}
                  <div style={{ marginTop: '6px' }}>
                    <Input
                      placeholder="Buscar productos..."
                      prefix={<SearchOutlined />}
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      allowClear
                      size="middle"
                      className="product-search-input"
                    />
                  </div>
                </div>

                {seatBar}

                {/* Products Grid - Scrollable */}
                <div style={{
                  flex: 1,
                  overflow: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  padding: '8px 12px',
                  minHeight: 0
                }} className="product-grid-container">
                  {productsLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <Spin size="large" />
                      <div style={{ marginTop: '16px' }}>Cargando productos...</div>
                    </div>
                  ) : (
                    <ProductGrid
                      products={filteredProducts}
                      onAddToOrder={handleProductAdd}
                      cartQuantities={cartQuantities}
                      loading={productsLoading}
                      compact={true}
                    />
                  )}
                </div>
              </div>

              {/* Fixed Bottom Order Panel - Always visible when there are items */}
              {totalNewItems > 0 && (
                <div style={{
                  borderTop: '2px solid var(--border-color)',
                  backgroundColor: 'var(--bg-primary)',
                  boxShadow: '0 -2px 8px var(--shadow-color)',
                  maxHeight: '35vh',
                  minHeight: '140px',
                  display: 'flex',
                  flexDirection: 'column',
                  flexShrink: 0
                }}>
                  {/* Order Summary Header */}
                  <div style={{
                    padding: '10px 12px',
                    borderBottom: '1px solid var(--border-color)',
                    backgroundColor: 'var(--bg-secondary)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0
                  }}>
                    <div>
                      <Text style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'block' }}>
                        {totalNewItems} {totalNewItems === 1 ? 'producto' : 'productos'}
                      </Text>
                      <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                        ${(totalNewAmount || 0).toFixed(2)}
                      </Text>
                    </div>
                    <Button
                      type="primary"
                      loading={isSending || createOrderLoading || addOrderItemLoading || addMultipleOrderItemsLoading}
                      onClick={handleSendToKitchen}
                      style={{
                        backgroundColor: '#52c41a',
                        borderColor: '#52c41a',
                        height: '40px',
                        fontSize: '13px',
                        fontWeight: 'bold'
                      }}
                      icon={<ShoppingCartOutlined />}
                    >
                      Enviar
                    </Button>
                  </div>

                  {/* Order Items List - Scrollable */}
                  <div style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: '8px 12px',
                    minHeight: 0
                  }}>
                    {localOrderItems.map((item, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 0',
                        borderBottom: index < localOrderItems.length - 1 ? '1px solid var(--border-color)' : 'none',
                        gap: '8px'
                      }}>
                        <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => handleEditItem(index)}>
                          <Text style={{ fontSize: '13px', fontWeight: '500', display: 'block' }}>
                            {showSeatBar && (
                              <span
                                role="button"
                                title="Cambiar comensal"
                                onClick={(e) => { e.stopPropagation(); handleCycleSeat(index); }}
                                style={{ marginRight: 6 }}
                              >
                                <SeatPill seatNumber={item.seatNumber} showShared size="small" />
                              </span>
                            )}
                            {item.quantity}x {item.productName}
                          </Text>
                          {item.dailyMenuSelections && item.dailyMenuSelections.length > 0 ? (
                            <Text style={{
                              fontSize: '11px',
                              color: 'var(--color-neutral-text)',
                              display: 'block',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {item.dailyMenuSelections.map(s => s.optionName || s.courseName).join(', ')}
                            </Text>
                          ) : item.selectedVariants.length > 0 && (
                            <Text style={{
                              fontSize: '11px',
                              color: 'var(--color-neutral-text)',
                              display: 'block',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {item.selectedVariants.map(v => v.name).join(', ')}
                            </Text>
                          )}
                          {item.notes && (
                            <Text style={{ fontSize: '11px', color: '#fa8c16', display: 'block', fontStyle: 'italic' }}>
                              Nota: {item.notes}
                            </Text>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                          <Text style={{ fontSize: '13px', color: '#52c41a', fontWeight: 'bold' }}>
                            ${(item.quantity * item.unitPrice).toFixed(2)}
                          </Text>
                          <Button
                            size="small"
                            type="text"
                            onClick={() => handleEditItem(index)}
                            style={{
                              width: '24px',
                              height: '24px',
                              fontSize: '14px',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#1890ff'
                            }}
                          >
                            ✎
                          </Button>
                          <Button
                            size="small"
                            danger
                            onClick={() => handleRemoveItem(index)}
                            style={{
                              width: '24px',
                              height: '24px',
                              fontSize: '14px',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ×
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Already-sent items, collapsed for context */}
                  {existingItemsSection}
                </div>
              )}
            </div>
            </>
          ) : (
            // Desktop Layout - Two columns from top to bottom
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
              {/* Left Column - Header + Products */}
              <div style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                borderRight: '1px solid var(--border-color)'
              }}>
                {/* Left Header */}
                <div style={{
                  padding: '12px 24px',
                  borderBottom: '1px solid var(--border-color)',
                  backgroundColor: 'var(--bg-secondary)',
                  flexShrink: 0
                }}>
                  <Space size="middle">
                    <Button
                      icon={<ArrowLeftOutlined />}
                      onClick={handleCancel}
                      type="text"
                    />
                    <ShoppingCartOutlined style={{ fontSize: '20px', color: 'var(--color-info)' }} />
                    <Title level={4} style={{ margin: 0 }}>
                      Comandear - Mesa {table?.number}
                    </Title>
                    <Text type="secondary">({table?.areaName})</Text>
                    {activeOrder?.openTime && (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Abierta {formatInTimeZone(activeOrder.openTime, 'HH:mm')}
                      </Text>
                    )}
                  </Space>
                </div>

                {/* Kitchen Notes - Desktop */}
                {kitchenNotes.length > 0 && (
                  <div
                    style={{
                      margin: '6px 16px 0 16px',
                      borderRadius: 6,
                      border: '1px solid #faad14',
                      backgroundColor: 'var(--bg-warning-light)',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}
                  >
                    <div
                      onClick={() => setKitchenNotesExpanded(!kitchenNotesExpanded)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 12px',
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                    >
                      <WarningOutlined style={{ fontSize: 16, color: '#faad14' }} />
                      <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
                        Avisos de Cocina ({kitchenNotes.length})
                      </span>
                      {kitchenNotesExpanded
                        ? <UpOutlined style={{ fontSize: 10, color: 'var(--color-neutral-text)' }} />
                        : <DownOutlined style={{ fontSize: 10, color: 'var(--color-neutral-text)' }} />
                      }
                    </div>
                    {kitchenNotesExpanded && (
                      <ul style={{
                        margin: 0,
                        paddingLeft: 36,
                        paddingBottom: 8,
                        paddingRight: 12,
                        fontSize: 12,
                        maxHeight: 120,
                        overflowY: 'auto',
                      }}>
                        {kitchenNotes.map(note => (
                          <li key={note.id} style={{ marginBottom: 1, lineHeight: 1.3 }}>{note.message}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Category Selector */}
                <div style={{ padding: '4px 16px 10px 16px', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <CategorySelector
                        categories={categories}
                        selectedCategoryId={selectedCategoryId}
                        onCategoryChange={handleCategoryChange}
                        loading={categoriesLoading}
                        compact={true}
                        horizontalScroll={true}
                      />
                    </div>
                    {hasActiveDailyMenu && (
                      <Button
                        type="primary"
                        onClick={() => setDailyMenuModalVisible(true)}
                        style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16', fontWeight: 'bold', flexShrink: 0 }}
                        size="large"
                      >
                        Menu del Dia
                      </Button>
                    )}
                  </div>

                  {/* Search Box */}
                  <div style={{ marginTop: '6px' }}>
                    <Input
                      placeholder="Buscar productos..."
                      prefix={<SearchOutlined />}
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      allowClear
                      size="middle"
                      className="product-search-input"
                    />
                  </div>
                </div>

                {seatBar}

                {/* Products Grid */}
                <div style={{ flex: 1, overflow: 'auto' }} className="product-grid-container">
                  {productsLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <Spin size="large" />
                      <div style={{ marginTop: '16px' }}>Cargando productos...</div>
                    </div>
                  ) : (
                    <ProductGrid
                      products={filteredProducts}
                      onAddToOrder={handleProductAdd}
                      cartQuantities={cartQuantities}
                      loading={productsLoading}
                      compact={false}
                    />
                  )}
                </div>
              </div>

              {/* Right Column - Order Summary (full height) */}
              <div style={{
                width: window.innerWidth > 1200 ? '450px' : '380px',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'var(--bg-secondary)',
                overflow: 'hidden',
                borderTopRightRadius: '8px',
              }}>
                {/* Right Header with Enviar button */}
                <div style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--border-color)',
                  backgroundColor: 'var(--bg-secondary)',
                  flexShrink: 0,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      Resumen de Orden
                    </Title>
                    {totalNewItems > 0 && (
                      <Text style={{ color: '#52c41a', fontSize: '12px' }}>
                        {totalNewItems} producto(s) · ${totalNewAmount.toFixed(2)}
                      </Text>
                    )}
                  </div>
                  {totalNewItems > 0 ? (
                    <Button
                      type="primary"
                      loading={isSending || createOrderLoading || addOrderItemLoading || addMultipleOrderItemsLoading}
                      onClick={handleSendToKitchen}
                      style={{
                        backgroundColor: '#52c41a',
                        borderColor: '#52c41a',
                        boxShadow: '0 2px 4px rgba(82, 196, 26, 0.3)'
                      }}
                    >
                      Enviar Comanda ({totalNewItems})
                    </Button>
                  ) : (
                    <Button type="default" disabled>
                      Agregar productos
                    </Button>
                  )}
                </div>

                {/* Scrollable Order Items */}
                <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                  <OrderSummary
                    order={activeOrder || null}
                    newItems={localOrderItems}
                    onRemoveNewItem={handleRemoveItem}
                    onUpdateNewItemQuantity={handleUpdateQuantity}
                    onEditNewItem={handleEditItem}
                    showActions={false}
                    showExistingItems={false}
                    showSeats={showSeatBar}
                    onCycleNewItemSeat={handleCycleSeat}
                    loading={orderLoading}
                  />
                </div>

                {/* Already-sent items, collapsed for context */}
                {existingItemsSection}

              </div>
            </div>
          )}
        </div>

      {/* Product Variants Modal */}
      {selectedProduct && (
        <ProductVariantsModal
          visible={variantsModalVisible}
          onCancel={() => { setVariantsModalVisible(false); clearEditState(); }}
          onConfirm={handleVariantsSubmit}
          product={selectedProduct}
          initialValues={editingItemIndex !== null && !editingDailyMenu ? {
            quantity: localOrderItems[editingItemIndex].quantity,
            notes: localOrderItems[editingItemIndex].notes || '',
            variantSelections: localOrderItems[editingItemIndex].variantSelections,
          } : undefined}
        />
      )}

      {/* Daily Menu Selection Modal */}
      <DailyMenuSelectionModal
        visible={dailyMenuModalVisible || editingDailyMenu}
        onCancel={() => { setDailyMenuModalVisible(false); setEditingDailyMenu(false); clearEditState(); }}
        onAddItem={handleDailyMenuAdd}
        initialValues={editingItemIndex !== null && editingDailyMenu ? {
          dailyMenuId: localOrderItems[editingItemIndex].dailyMenuId!,
          quantity: 1,
          notes: localOrderItems[editingItemIndex].notes,
          dailyMenuSelections: localOrderItems[editingItemIndex].dailyMenuSelections!,
        } : undefined}
      />
    </>
  );
};

interface ComandearModalProps {
  visible: boolean;
  onCancel: () => void;
  table?: Table;
}

const ComandearModal: React.FC<ComandearModalProps> = ({
  visible,
  onCancel,
  table,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 768);
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={isMobile ? "100%" : "95%"}
      style={{
        top: isMobile ? 0 : 20,
        maxWidth: isMobile ? "100%" : 1400,
        margin: isMobile ? 0 : undefined
      }}
      styles={{
        body: {
          padding: 0,
          height: isMobile ? '100vh' : 'calc(100vh - 100px)'
        }
      }}
      destroyOnClose
    >
      <ComandearContent table={table} visible={visible} onClose={onCancel} />
    </Modal>
  );
};

export default ComandearModal;